import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const statusClassMap = {
  connected: 'badge-success',
  pending: 'badge-warning',
  needs_reauth: 'badge-warning',
  error: 'badge-danger',
  disconnected: 'badge-danger',
};

const healthClassMap = {
  healthy: 'badge-success',
  warning: 'badge-warning',
  error: 'badge-danger',
  unknown: 'badge-info',
};

const operationalClassMap = {
  healthy: 'badge-success',
  attention: 'badge-warning',
  action_required: 'badge-danger',
};

const domainOptions = [
  { value: 'accounts', label: 'Contas' },
  { value: 'cards', label: 'Cartoes' },
  { value: 'transactions', label: 'Transacoes' },
  { value: 'investments', label: 'Investimentos' },
];

const defaultForm = {
  provider_type: 'manual',
  provider_key: 'manual',
  display_name: '',
  sync_frequency_minutes: 360,
  data_domains: ['accounts', 'cards', 'transactions'],
  seed_accounts_text: '',
  seed_card_accounts_text: '',
  seed_account_transactions_text: '',
  seed_card_transactions_text: '',
};

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString('pt-BR') : 'Nao disponivel';
}

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString('pt-BR') : 'Nao disponivel';
}

function formatRelativeDays(value) {
  if (value === null || value === undefined) return 'Nao aplicavel';
  if (Number(value) < 0) return `Expirado ha ${Math.abs(Number(value))}d`;
  if (Number(value) === 0) return 'Expira hoje';
  if (Number(value) === 1) return 'Expira em 1d';
  return `Expira em ${Number(value)}d`;
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const normalized = String(value).trim().replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseSeedAccounts(text) {
  return String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [display_name, account_type, current_balance, available_balance, institution_name] = line.split('|').map((item) => item.trim());
      return {
        display_name,
        account_type: account_type || 'checking',
        current_balance: toNumber(current_balance) ?? 0,
        available_balance: toNumber(available_balance),
        institution_name: institution_name || null,
        metadata: {
          seed_source: 'connections_page',
        },
      };
    })
    .filter((item) => item.display_name);
}

function parseSeedCardAccounts(text) {
  return String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [
        display_name,
        credit_limit,
        available_limit,
        current_statement_amount,
        next_statement_amount,
        closing_day,
        due_day,
        brand,
        last4,
        institution_name,
      ] = line.split('|').map((item) => item.trim());

      return {
        display_name,
        credit_limit: toNumber(credit_limit),
        available_limit: toNumber(available_limit),
        current_statement_amount: toNumber(current_statement_amount) ?? 0,
        next_statement_amount: toNumber(next_statement_amount) ?? 0,
        closing_day: toNumber(closing_day),
        due_day: toNumber(due_day),
        brand: brand || null,
        last4: last4 || null,
        institution_name: institution_name || null,
        metadata: {
          seed_source: 'connections_page',
        },
      };
    })
    .filter((item) => item.display_name);
}

function parseSeedAccountTransactions(text) {
  return String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [
        account_ref,
        description,
        direction,
        amount,
        booked_at,
        merchant_name,
        category,
        external_transaction_id,
      ] = line.split('|').map((item) => item.trim());

      return {
        account_ref: account_ref || null,
        description,
        direction: direction || 'debit',
        amount: toNumber(amount) ?? 0,
        booked_at: booked_at || undefined,
        merchant_name: merchant_name || undefined,
        category: category || undefined,
        external_transaction_id: external_transaction_id || undefined,
        metadata: {
          seed_source: 'connections_page',
          refinement_layer: true,
        },
      };
    })
    .filter((item) => item.description);
}

function parseSeedCardTransactions(text) {
  return String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [
        card_ref,
        description,
        amount_total,
        installment_count,
        purchase_date,
        merchant_name,
        category,
        external_transaction_id,
      ] = line.split('|').map((item) => item.trim());

      return {
        card_ref: card_ref || null,
        description,
        amount_total: toNumber(amount_total) ?? 0,
        installment_count: Number(installment_count || 1),
        purchase_date: purchase_date || undefined,
        merchant_name: merchant_name || undefined,
        category: category || undefined,
        external_transaction_id: external_transaction_id || undefined,
        metadata: {
          seed_source: 'connections_page',
          refinement_layer: true,
        },
      };
    })
    .filter((item) => item.description);
}

const PersonalFinanceConnectionsPage = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [syncingId, setSyncingId] = useState('');
  const [consentingId, setConsentingId] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [connections, setConnections] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [cardAccounts, setCardAccounts] = useState([]);
  const [healthCenter, setHealthCenter] = useState({ summary: {}, urgent_items: [] });
  const [selectedConnectionId, setSelectedConnectionId] = useState('');
  const [syncRuns, setSyncRuns] = useState([]);
  const [form, setForm] = useState(defaultForm);

  const loadData = async (preferredConnectionId = '') => {
    try {
      setLoading(true);
      const [connectionsResponse, accountsResponse, cardAccountsResponse, healthCenterResponse] = await Promise.all([
        axios.get('/api/personal-finance/connections'),
        axios.get('/api/personal-finance/accounts'),
        axios.get('/api/personal-finance/card-accounts'),
        axios.get('/api/personal-finance/connections/health-center'),
      ]);

      const nextConnections = Array.isArray(connectionsResponse.data) ? connectionsResponse.data : [];
      const nextAccounts = Array.isArray(accountsResponse.data) ? accountsResponse.data : [];
      const nextCardAccounts = Array.isArray(cardAccountsResponse.data) ? cardAccountsResponse.data : [];
      const nextSelectedConnectionId =
        preferredConnectionId ||
        (nextConnections.some((item) => item.id === selectedConnectionId) ? selectedConnectionId : nextConnections[0]?.id || '');

      setConnections(nextConnections);
      setAccounts(nextAccounts);
      setCardAccounts(nextCardAccounts);
      setHealthCenter(healthCenterResponse.data || { summary: {}, urgent_items: [] });
      setSelectedConnectionId(nextSelectedConnectionId);

      if (nextSelectedConnectionId) {
        await loadSyncRuns(nextSelectedConnectionId);
      } else {
        setSyncRuns([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar conexoes');
    } finally {
      setLoading(false);
    }
  };

  const loadSyncRuns = async (connectionId) => {
    if (!connectionId) {
      setSyncRuns([]);
      return;
    }

    try {
      setHistoryLoading(true);
      const response = await axios.get(`/api/personal-finance/connections/${connectionId}/sync-runs`);
      setSyncRuns(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar historico de sync');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const selectedConnection = connections.find((item) => item.id === selectedConnectionId) || null;
  const selectedAccounts = accounts.filter((item) => item.connection_id === selectedConnectionId);
  const selectedCardAccounts = cardAccounts.filter((item) => item.connection_id === selectedConnectionId);

  const handleFormChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleDomainToggle = (value) => {
    setForm((current) => {
      const hasValue = current.data_domains.includes(value);
      return {
        ...current,
        data_domains: hasValue
          ? current.data_domains.filter((item) => item !== value)
          : [...current.data_domains, value],
      };
    });
  };

  const handleCreateConnection = async () => {
    try {
      setSubmitting(true);

      const connectionPayload = {
        provider_type: form.provider_type,
        provider_key: form.provider_key,
        display_name: form.display_name,
        sync_frequency_minutes: Number(form.sync_frequency_minutes || 360),
        data_domains: form.data_domains,
      };

      const accountsSeed = parseSeedAccounts(form.seed_accounts_text);
      const cardAccountsSeed = parseSeedCardAccounts(form.seed_card_accounts_text);
      const accountTransactionsSeed = parseSeedAccountTransactions(form.seed_account_transactions_text);
      const cardTransactionsSeed = parseSeedCardTransactions(form.seed_card_transactions_text);

      const createResponse = await axios.post('/api/personal-finance/connections', connectionPayload);
      const createdConnection = createResponse.data?.connection;

      if (!createdConnection?.id) {
        throw new Error('Conexao criada sem identificador');
      }

      if (
        (
          accountsSeed.length > 0 ||
          cardAccountsSeed.length > 0 ||
          accountTransactionsSeed.length > 0 ||
          cardTransactionsSeed.length > 0
        ) &&
        form.provider_type === 'manual'
      ) {
        await axios.post(`/api/personal-finance/connections/${createdConnection.id}/sync`, {
          trigger_type: 'manual',
          seed_accounts: accountsSeed,
          seed_card_accounts: cardAccountsSeed,
          seed_account_transactions: accountTransactionsSeed,
          seed_card_transactions: cardTransactionsSeed,
        });
      }

      setForm({
        ...defaultForm,
        provider_type: form.provider_type,
        provider_key: form.provider_type === 'manual' ? 'manual' : 'open_finance_mock',
      });

      toast.success('Conexao criada');
      await loadData(createdConnection.id);
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Erro ao criar conexao');
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivateConsent = async (connection) => {
    try {
      setConsentingId(connection.id);
      await axios.post(`/api/personal-finance/connections/${connection.id}/consents`, {
        status: 'active',
        scope_domains: connection.data_domains?.length ? connection.data_domains : ['accounts', 'cards'],
        expires_at: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
        metadata: {
          source: 'manual_mock',
        },
      });
      toast.success('Consentimento ativado');
      await loadData(connection.id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao ativar consentimento');
    } finally {
      setConsentingId('');
    }
  };

  const handleExpireConsent = async (connection) => {
    try {
      setConsentingId(connection.id);
      await axios.post(`/api/personal-finance/connections/${connection.id}/consents`, {
        status: 'expired',
        scope_domains: connection.data_domains?.length ? connection.data_domains : ['accounts', 'cards', 'transactions'],
        expires_at: new Date().toISOString(),
        metadata: {
          source: 'manual_mock_expire',
        },
      });
      toast.success('Consentimento marcado como expirado');
      await loadData(connection.id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao expirar consentimento');
    } finally {
      setConsentingId('');
    }
  };

  const handleRunSync = async (connection) => {
    try {
      setSyncingId(connection.id);
      await axios.post(`/api/personal-finance/connections/${connection.id}/sync`, {
        trigger_type: 'manual',
      });
      toast.success('Sync executado');
      await loadData(connection.id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao sincronizar conexao');
    } finally {
      setSyncingId('');
    }
  };

  const summaryCards = [
    {
      label: 'Conexoes cadastradas',
      value: String(connections.length),
      helper: 'Control plane do modulo pessoal',
    },
    {
      label: 'Acao imediata',
      value: String(healthCenter.summary?.action_required_connections || 0),
      helper: 'Reautorizacao, expiracao ou sync alem da janela',
    },
    {
      label: 'Em observacao',
      value: String(healthCenter.summary?.attention_connections || 0),
      helper: 'Conexoes com stale sync, expiracao proxima ou warning',
    },
    {
      label: 'Backoff ativo',
      value: String(healthCenter.summary?.backoff_active_connections || 0),
      helper: 'Protege o sync automatico apos falhas consecutivas',
    },
    {
      label: 'Consentimentos expirando',
      value: String(healthCenter.summary?.expiring_consents || 0),
      helper: 'Janela curta antes de cair em reauth',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {summaryCards.map((item) => (
          <div key={item.label} className="card">
            <div className="card-body space-y-2">
              <p className="text-sm text-slate-500">{item.label}</p>
              <p className="text-2xl font-semibold text-slate-900">{loading ? '...' : item.value}</p>
              <p className="text-sm text-slate-500">{item.helper}</p>
            </div>
          </div>
        ))}
      </div>

      {(healthCenter.summary?.action_required_connections > 0 || healthCenter.summary?.attention_connections > 0) && (
        <div className="rounded-3xl border border-rose-200 bg-rose-50/80 p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-700">Health center</p>
              <p className="mt-2 text-xl font-semibold text-slate-900">
                {healthCenter.summary?.action_required_connections > 0
                  ? `${healthCenter.summary.action_required_connections} conexoes exigem acao imediata`
                  : `${healthCenter.summary?.attention_connections || 0} conexoes merecem monitoramento`}
              </p>
              <p className="mt-2 text-sm text-slate-600">
                Este slice adiciona lifecycle de consentimento, stale sync, backoff e priorizacao operacional dentro do modulo pessoal.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-rose-200 bg-white p-4 text-sm">
                <p className="text-slate-500">Consents expirados</p>
                <p className="mt-1 text-lg font-semibold text-slate-900">{healthCenter.summary?.expired_consents || 0}</p>
              </div>
              <div className="rounded-2xl border border-rose-200 bg-white p-4 text-sm">
                <p className="text-slate-500">Syncs stale</p>
                <p className="mt-1 text-lg font-semibold text-slate-900">{healthCenter.summary?.stale_connections || 0}</p>
              </div>
              <div className="rounded-2xl border border-rose-200 bg-white p-4 text-sm">
                <p className="text-slate-500">Backoff</p>
                <p className="mt-1 text-lg font-semibold text-slate-900">{healthCenter.summary?.backoff_active_connections || 0}</p>
              </div>
            </div>
          </div>
          {Array.isArray(healthCenter.urgent_items) && healthCenter.urgent_items.length > 0 && (
            <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-3">
              {healthCenter.urgent_items.slice(0, 3).map((item) => (
                <div key={item.connection_id} className="rounded-2xl border border-rose-200 bg-white p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-slate-900">{item.display_name}</p>
                    <span className={operationalClassMap[item.operational_status] || 'badge-info'}>{item.operational_status}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-500">{item.primary_issue}</p>
                  <p className="mt-2 text-sm text-rose-700">{item.recommended_action}</p>
                  {item.backoff_until_at && (
                    <p className="mt-2 text-xs text-slate-400">Backoff ate {formatDateTime(item.backoff_until_at)}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.95fr_1.35fr]">
        <div className="card">
          <div className="card-body space-y-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Connectivity operating layer</p>
              <h2 className="text-xl font-semibold text-slate-900">Nova conexao</h2>
              <p className="mt-2 text-sm text-slate-500">
                Este slice consolida consentimento, stale sync e backoff. A conexao nasce pronta para operar dentro do health center do modulo.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="space-y-2 text-sm">
                <span className="font-medium text-slate-700">Tipo de provider</span>
                <select
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  value={form.provider_type}
                  onChange={(event) => {
                    const nextType = event.target.value;
                    setForm((current) => ({
                      ...current,
                      provider_type: nextType,
                      provider_key: nextType === 'manual' ? 'manual' : 'open_finance_mock',
                    }));
                  }}
                >
                  <option value="manual">manual</option>
                  <option value="open_finance">open_finance</option>
                </select>
              </label>

              <label className="space-y-2 text-sm">
                <span className="font-medium text-slate-700">Provider key</span>
                <input
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  value={form.provider_key}
                  onChange={(event) => handleFormChange('provider_key', event.target.value)}
                  placeholder="manual, open_finance_mock, banco_x"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="space-y-2 text-sm">
                <span className="font-medium text-slate-700">Nome exibido</span>
                <input
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  value={form.display_name}
                  onChange={(event) => handleFormChange('display_name', event.target.value)}
                  placeholder="Banco principal, Cartao premium, Itaú mock"
                />
              </label>

              <label className="space-y-2 text-sm">
                <span className="font-medium text-slate-700">Frequencia de sync</span>
                <input
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  type="number"
                  min="5"
                  max="1440"
                  value={form.sync_frequency_minutes}
                  onChange={(event) => handleFormChange('sync_frequency_minutes', event.target.value)}
                />
              </label>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">Dominios de dados</p>
              <div className="grid grid-cols-2 gap-3">
                {domainOptions.map((item) => (
                  <label key={item.value} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={form.data_domains.includes(item.value)}
                      onChange={() => handleDomainToggle(item.value)}
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <label className="space-y-2 text-sm">
                <span className="font-medium text-slate-700">Seeds de contas</span>
                <textarea
                  className="min-h-[120px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  value={form.seed_accounts_text}
                  onChange={(event) => handleFormChange('seed_accounts_text', event.target.value)}
                  placeholder="Conta Principal|checking|4850.25|4700.12|Banco Central Mock"
                />
              </label>
              <p className="text-xs text-slate-500">Formato: nome|tipo|saldo_atual|saldo_disponivel|instituicao. So e usado no primeiro sync de conexoes manuais.</p>
            </div>

            <div className="space-y-3">
              <label className="space-y-2 text-sm">
                <span className="font-medium text-slate-700">Seeds de cartoes</span>
                <textarea
                  className="min-h-[120px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  value={form.seed_card_accounts_text}
                  onChange={(event) => handleFormChange('seed_card_accounts_text', event.target.value)}
                  placeholder="Cartao Black|12000|9500|1800|2200|25|5|Visa|1234|Banco Central Mock"
                />
              </label>
              <p className="text-xs text-slate-500">Formato: nome|limite|limite_disponivel|fatura_atual|fatura_projetada|fechamento|vencimento|bandeira|final4|instituicao.</p>
            </div>

            <div className="space-y-3">
              <label className="space-y-2 text-sm">
                <span className="font-medium text-slate-700">Seeds de transacoes de conta</span>
                <textarea
                  className="min-h-[120px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  value={form.seed_account_transactions_text}
                  onChange={(event) => handleFormChange('seed_account_transactions_text', event.target.value)}
                  placeholder="Conta Principal|Salario empresa|credit|7200|2026-03-05|Empresa XPTO|income|acc-tx-001"
                />
              </label>
              <p className="text-xs text-slate-500">
                Formato: conta_ref|descricao|direcao|valor|data|merchant|categoria|external_id. Se houver apenas uma conta na conexao, `conta_ref` pode ficar vazio.
              </p>
            </div>

            <div className="space-y-3">
              <label className="space-y-2 text-sm">
                <span className="font-medium text-slate-700">Seeds de transacoes de cartao</span>
                <textarea
                  className="min-h-[120px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  value={form.seed_card_transactions_text}
                  onChange={(event) => handleFormChange('seed_card_transactions_text', event.target.value)}
                  placeholder="Cartao Black|Spotify Premium|27.9|1|2026-03-08|Spotify|subscriptions|card-tx-001"
                />
              </label>
              <p className="text-xs text-slate-500">
                Formato: cartao_ref|descricao|valor_total|parcelas|data_compra|merchant|categoria|external_id. Se houver apenas um cartao na conexao, `cartao_ref` pode ficar vazio.
              </p>
            </div>

            <button className="btn-primary w-full" onClick={handleCreateConnection} disabled={submitting}>
              {submitting ? 'Criando conexao...' : 'Criar conexao'}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Control plane</p>
                <h2 className="text-xl font-semibold text-slate-900">Conexoes do household</h2>
              </div>
              <button className="btn-secondary" onClick={() => loadData(selectedConnectionId)} disabled={loading}>Atualizar</button>
            </div>

            {!loading && connections.length === 0 && (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6">
                <p className="text-sm font-medium text-slate-900">Nenhuma conexao cadastrada ainda</p>
                <p className="mt-2 text-sm text-slate-500">
                  Crie uma conexao manual para validar o bounded context ou uma open_finance para testar consentimento e health.
                </p>
              </div>
            )}

            <div className="space-y-3">
              {connections.map((connection) => (
                <div
                  key={connection.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setSelectedConnectionId(connection.id);
                    loadSyncRuns(connection.id);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedConnectionId(connection.id);
                      loadSyncRuns(connection.id);
                    }
                  }}
                  className={`w-full rounded-2xl border p-4 text-left transition ${
                    selectedConnectionId === connection.id ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{connection.display_name}</p>
                      <p className="mt-1 text-sm text-slate-500">
                        {connection.provider_type} / {connection.provider_key}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className={operationalClassMap[connection.operational_status] || 'badge-info'}>{connection.operational_status}</span>
                      <span className={statusClassMap[connection.status] || 'badge-info'}>{connection.status}</span>
                      <span className={healthClassMap[connection.health_status] || 'badge-info'}>{connection.health_status}</span>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-2 text-sm text-slate-600 md:grid-cols-2">
                    <span>Health score: {connection.health_score ?? 0}</span>
                    <span>Proximo sync: {formatDateTime(connection.next_sync_at)}</span>
                    <span>Contas: {connection.accounts_count || 0}</span>
                    <span>Cartoes: {connection.card_accounts_count || 0}</span>
                    <span>Ultimo sync: {formatDateTime(connection.last_successful_sync_at || connection.last_synced_at)}</span>
                    <span>Consentimento: {connection.consent_expires_at ? formatDate(connection.consent_expires_at) : 'Nao aplicavel'}</span>
                    <span>Consent lifecycle: {connection.consent_lifecycle?.required ? `${connection.consent_lifecycle?.state || 'unknown'} / ${formatRelativeDays(connection.consent_lifecycle?.days_until_expiry)}` : 'Nao requerido'}</span>
                    <span>Sync monitor: {connection.sync_monitor?.state || 'unknown'}</span>
                    <span>Falhas seguidas: {connection.resilience?.consecutive_failures || 0}</span>
                    <span>Trend recente: {connection.sync_monitor?.recent_trend?.stability || 'stable'}</span>
                  </div>

                  {(connection.primary_issue || connection.recommended_action) && (
                    <div className="mt-3 rounded-2xl border border-amber-100 bg-amber-50 px-3 py-3 text-sm">
                      <p className="font-medium text-slate-900">{connection.primary_issue || 'Sem issue primaria'}</p>
                      <p className="mt-1 text-slate-600">{connection.recommended_action || 'Sem acao recomendada'}</p>
                      {connection.resilience?.backoff_active && (
                        <p className="mt-1 text-xs text-slate-400">Backoff automatico ate {formatDateTime(connection.resilience.backoff_until_at)}</p>
                      )}
                    </div>
                  )}

                  {!!connection.last_error_message && (
                    <p className="mt-3 rounded-2xl border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                      Ultimo erro: {connection.last_error_message}
                    </p>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    {connection.provider_type === 'open_finance' && (
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleActivateConsent(connection);
                        }}
                        disabled={consentingId === connection.id}
                      >
                        {consentingId === connection.id ? 'Processando...' : connection.consent_lifecycle?.state === 'active' ? 'Renovar consentimento' : 'Ativar consentimento'}
                      </button>
                    )}
                    {connection.provider_type === 'open_finance' && connection.consent_lifecycle?.state === 'active' && (
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleExpireConsent(connection);
                        }}
                        disabled={consentingId === connection.id}
                      >
                        {consentingId === connection.id ? 'Processando...' : 'Simular expiracao'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRunSync(connection);
                      }}
                      disabled={syncingId === connection.id}
                    >
                      {syncingId === connection.id ? 'Sincronizando...' : 'Rodar sync'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.95fr_1.05fr_1.05fr]">
        <div className="card">
          <div className="card-body space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Historico</p>
              <h2 className="text-xl font-semibold text-slate-900">Sync runs</h2>
            </div>

            {!selectedConnection && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                Selecione uma conexao para ver o historico de sincronizacao.
              </div>
            )}

            {selectedConnection && (
              <div className="space-y-3">
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{selectedConnection.display_name}</p>
                      <p className="mt-1 text-sm text-slate-500">{selectedConnection.primary_issue || 'Sem issue operacional aberta'}</p>
                    </div>
                    <span className={operationalClassMap[selectedConnection.operational_status] || 'badge-info'}>
                      {selectedConnection.operational_status}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-600 md:grid-cols-2">
                    <span>Consent: {selectedConnection.consent_lifecycle?.state || 'na'}</span>
                    <span>Sync: {selectedConnection.sync_monitor?.state || 'na'}</span>
                    <span>Trend: {selectedConnection.sync_monitor?.recent_trend?.stability || 'stable'}</span>
                    <span>Falhas: {selectedConnection.resilience?.consecutive_failures || 0}</span>
                  </div>
                </div>
                {historyLoading && <p className="text-sm text-slate-500">Carregando historico...</p>}
                {!historyLoading && syncRuns.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                    Nenhum sync executado para esta conexao.
                  </div>
                )}
                {syncRuns.map((run) => (
                  <div key={run.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-medium text-slate-900">{run.trigger_type}</p>
                      <span className={statusClassMap[run.status] || healthClassMap[run.health_status] || 'badge-info'}>{run.status}</span>
                    </div>
                    <div className="mt-3 space-y-1 text-sm text-slate-600">
                      <p>Inicio: {formatDateTime(run.started_at)}</p>
                      <p>Fim: {formatDateTime(run.finished_at)}</p>
                      <p>Duracao: {run.duration_ms ? `${run.duration_ms} ms` : 'Nao disponivel'}</p>
                      <p>Health: {run.health_status} / {run.health_score}</p>
                      <p>
                        Processado: {(run.stats_json?.accounts_processed || 0)} contas, {(run.stats_json?.card_accounts_processed || 0)} cartoes, {(run.stats_json?.account_transactions_processed || 0)} transacoes de conta, {(run.stats_json?.card_transactions_processed || 0)} transacoes de cartao
                      </p>
                      <p>
                        Criado: {(run.stats_json?.account_transactions_created || 0)} transacoes de conta e {(run.stats_json?.card_transactions_created || 0)} transacoes de cartao
                      </p>
                      <p>
                        Refinamento: {(run.stats_json?.transaction_duplicates_skipped || 0)} duplicatas ignoradas, {(run.stats_json?.merchants_normalized || 0)} merchants normalizados, {(run.stats_json?.categories_inferred || 0)} categorias inferidas
                      </p>
                    </div>
                    {!!run.error_message && (
                      <p className="mt-3 rounded-2xl border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                        {run.error_message}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-body space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Dados canonicos</p>
              <h2 className="text-xl font-semibold text-slate-900">Contas conectadas</h2>
            </div>

            {!selectedConnection && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                Selecione uma conexao para inspecionar as contas.
              </div>
            )}

            {selectedConnection && (
              <div className="space-y-3">
                {selectedAccounts.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                    Nenhuma conta canonica criada para esta conexao.
                  </div>
                )}
                {selectedAccounts.map((account) => (
                  <div key={account.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{account.display_name}</p>
                        <p className="mt-1 text-sm text-slate-500">
                          {account.institution_name || account.connection_display_name} / {account.account_type}
                        </p>
                      </div>
                      <span className="badge-info">{account.currency}</span>
                    </div>
                    <div className="mt-3 space-y-1 text-sm text-slate-600">
                      <p>Saldo atual: {currency.format(Number(account.current_balance || 0))}</p>
                      <p>Saldo disponivel: {account.available_balance === null ? 'Nao informado' : currency.format(Number(account.available_balance || 0))}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-body space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Dados canonicos</p>
              <h2 className="text-xl font-semibold text-slate-900">Cartoes conectados</h2>
            </div>

            {!selectedConnection && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                Selecione uma conexao para inspecionar os cartoes.
              </div>
            )}

            {selectedConnection && (
              <div className="space-y-3">
                {selectedCardAccounts.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                    Nenhum cartao canonico criado para esta conexao.
                  </div>
                )}
                {selectedCardAccounts.map((card) => (
                  <div key={card.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{card.display_name}</p>
                        <p className="mt-1 text-sm text-slate-500">
                          {card.brand || 'Sem bandeira'} / final {card.last4 || '----'}
                        </p>
                      </div>
                      <span className="badge-info">{card.institution_name || card.connection_display_name}</span>
                    </div>
                    <div className="mt-3 space-y-1 text-sm text-slate-600">
                      <p>Limite: {card.credit_limit === null ? 'Nao informado' : currency.format(Number(card.credit_limit || 0))}</p>
                      <p>Disponivel: {card.available_limit === null ? 'Nao informado' : currency.format(Number(card.available_limit || 0))}</p>
                      <p>Fatura atual: {currency.format(Number(card.current_statement_amount || 0))}</p>
                      <p>Proxima fatura: {currency.format(Number(card.next_statement_amount || 0))}</p>
                      <p>Fechamento / vencimento: {card.closing_day || '--'} / {card.due_day || '--'}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalFinanceConnectionsPage;
